// Story 7.16 (ADO-1373) — the Stock Card read client (AC1, AC2, AC3, AC5).
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// store-stock-movement module's Stock Card read (Story 14.15 / ADO-1376, AC6).
// Mirrors `availability.client.ts`: the SINGLE consumer seam until the
// `openapi-typescript`-generated client lands, exercised against msw in tests.
//
// ROUTE: `/api/v1/store-stock/stock-card`. The 14.15 controller docblock says
// "bare route, no /api/v1 prefix (the global prefix is deferred repo-wide)", and
// that sentence is STALE, not a contract: Bug 1181 applied the prefix repo-wide
// and `GLOBAL_PREFIX_EXCLUSIONS` (apps/api/src/global-prefix.ts) lists only
// `health` and `internal/auth0-log-stream`. `availability` — the precedent that
// docblock cites — is likewise served prefixed and its client uses `API_PREFIX`.
// The controller comment is corrected in this story's diff; this note records why.
//
// The wire shapes are a LOCK-STEP COPY of the module's exported `StockCardDto` /
// `StockCardWeekRow` (libs/modules/store-stock-movement/src/contracts/dtos/
// stock-card.dto.ts) — keep the two in sync.
//
// NEVER REJECTS. Every failure mode — no store code, no EAN, non-2xx, network
// error, unparseable body — resolves to `null`, which callers render as AC5's
// "absent data → row hidden, no error". A Stock Card is supplementary context on
// a product page; it must never turn a working page into an error state.

import { API_PREFIX, apiFetch } from './shoplink-api';

/** One metric row of the week table: a Monday->Sunday series + its total.
 *  Lock-step with the backend `StockCardWeekRow`. `null` = no movement that day. */
export interface StockCardWeekRow {
  readonly label: string;
  /** Exactly 7 entries, Monday (index 0) -> Sunday (index 6). */
  readonly days: readonly (number | null)[];
  readonly total: number;
}

/** Lock-step with the backend `StockCardDto`. */
export interface StockCardDto {
  /** Units sold in the current-week window. */
  readonly soldLast7d: number;
  /** The week table rows in render order (Sales, Sales Value, Sales 4wk Avg, …,
   *  Purchases, …). The server decides the set; the client renders what it gets. */
  readonly week: readonly StockCardWeekRow[];
}

export const STOCK_CARD_PATH = `${API_PREFIX}/store-stock/stock-card`;
/** Story 7.16 (ADO-1373) — the BATCH listing read (plural). */
export const STOCK_CARD_SUMMARIES_PATH = `${API_PREFIX}/store-stock/stock-cards`;

/** One row of the batch listing read — lock-step with the backend
 *  `StockCardSummaryDto`. A SKU the catalogue has no barcode for is ABSENT from
 *  the response; `soldLast7d: 0` is a real answer ("none sold"), not an absence. */
export interface StockCardSummary {
  readonly skuCode: string;
  readonly soldLast7d: number;
}

/** Code review follow-up (Story 7.16, ADO-1373): the server does NOT 400 a large
 *  `skus` batch — `getStockCardSummaries` never validates its size. This chunks
 *  requests purely to avoid over-fetching in one round trip, mirroring the
 *  availability seam's chunking. Lock-step tested (below) against the two other
 *  500s this value used to just claim to mirror in prose — `SALES_BATCH_SIZE`
 *  (store-stock-movement's `prisma-stock-card-read.repository.ts`) and
 *  `LOOKUP_BATCH_SIZE` (catalogue's `prisma-product-ean-lookup.adapter.ts`).
 *  Change this and the lock-step test fails; update all three together. */
export const MAX_STOCK_CARD_BATCH_SIZE = 500;

/**
 * Story 7.16 (ADO-1373) — read the "N sold (7d)" figure for a page of SKUs in ONE
 * request (per chunk). No surface reads it now: the listing card's sold teaser was
 * removed, and this reader stays as the client of the batch route it mirrors.
 *
 * This exists instead of calling {@link fetchStockCard} per card for two reasons,
 * both measured rather than assumed: a listing renders 20+ cards, and a listing
 * row carries no EAN, so the per-card shape would be TWO requests per card (a
 * product-detail read for the barcode, then the stock card). The batch route
 * resolves barcodes server-side through the catalogue.
 *
 * NEVER REJECTS — like its singular sibling, every failure resolves to an empty
 * list, which callers render as "no teaser figure" rather than an error (AC5).
 */
export async function fetchStockCardSummaries(
  storeCode: string,
  skuCodes: readonly string[],
): Promise<readonly StockCardSummary[]> {
  const store = storeCode.trim();
  const unique = [...new Set(skuCodes.map((sku) => sku.trim()))].filter((sku) => sku !== '');
  if (store === '' || unique.length === 0) return [];

  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += MAX_STOCK_CARD_BATCH_SIZE) {
    chunks.push(unique.slice(i, i + MAX_STOCK_CARD_BATCH_SIZE));
  }

  // Per-chunk isolation, mirroring `fetchAvailability`: one chunk's failure must
  // not discard another chunk's rows. A missing SKU simply renders no teaser
  // figure, so there is no "unread vs absent" distinction to preserve here —
  // unlike availability, nothing is gated on this number.
  const results = await Promise.all(
    chunks.map(async (chunk): Promise<readonly StockCardSummary[]> => {
      try {
        const url =
          `${STOCK_CARD_SUMMARIES_PATH}?storeCode=${encodeURIComponent(store)}` +
          `&skus=${encodeURIComponent(chunk.join(','))}`;
        const res = await apiFetch(url);
        if (!res.ok) return [];
        return (await res.json()) as StockCardSummary[];
      } catch {
        return [];
      }
    }),
  );
  return results.flat();
}

/** The read query. `asOf` is deliberately ABSENT: the server defaults the
 *  current-week window to the request instant, so the client constructs no date
 *  (project-context bans a bare `new Date()`). */
export interface StockCardQuery {
  /** The caller's store — `membership.users.customer_account_code` (part C). */
  readonly storeCode: string;
  /** The product's catalogue code (the deliveries join key). */
  readonly productCode: string;
  /**
   * The product's EAN code(s) — the sales join key. The client has only
   * `ProductDetail.ean` (one primary barcode), so in practice this is a
   * single-element array. Strictly NARROWER than SL1.0, which joins on
   * `EAN1|EAN2|EAN3`: a product whose EPOS rows are keyed under a secondary
   * barcode shows no sales here (Deviation D-4, documented, not claimed as parity).
   */
  readonly eanCodes: readonly string[];
}

/**
 * Read one product's Stock Card at one store, or `null` when there is nothing to
 * ask (no store code / no EAN) or the read did not succeed.
 *
 * FETCH ON DEMAND — call this when the panel opens, never per listing card. A
 * listing page renders 20+ cards and at most one Stock Card is open at a time.
 */
export async function fetchStockCard(query: StockCardQuery): Promise<StockCardDto | null> {
  const storeCode = query.storeCode.trim();
  const productCode = query.productCode.trim();
  // Dedupe + drop blanks the way the availability seam does, so a repeated or
  // padded EAN cannot lengthen the URL or reach the server's `parseEans`.
  const eanCodes = [...new Set(query.eanCodes.map((ean) => ean.trim()))].filter(
    (ean) => ean !== '',
  );

  // AC5 — nothing to ask about. The server would answer 400 (`storeCode` required,
  // at least one `ean` required), so this is a guard against a pointless failed
  // request, not a duplicate of server validation.
  if (storeCode === '' || productCode === '' || eanCodes.length === 0) return null;

  const url =
    `${STOCK_CARD_PATH}?storeCode=${encodeURIComponent(storeCode)}` +
    `&productCode=${encodeURIComponent(productCode)}` +
    `&ean=${encodeURIComponent(eanCodes.join(','))}`;

  try {
    const res = await apiFetch(url);
    if (!res.ok) return null;
    return (await res.json()) as StockCardDto;
  } catch {
    return null;
  }
}
